import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, X, Award, Star, GitCompare, ShoppingCart } from 'lucide-react';
import { CatalogAPI, formatPrice, formatRelative, freshnessColor } from '../lib/api';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

const loadIds = () => {
  try { return JSON.parse(localStorage.getItem('dz_compare') || '[]'); }
  catch { return []; }
};

const Compare = ({ onAddToCart }) => {
  const [ids, setIds] = useState(loadIds());
  const [items, setItems] = useState([]);
  const [offersByProduct, setOffersByProduct] = useState({});
  const [cartSupplierIds, setCartSupplierIds] = useState(() => {
    try {
      const cart = JSON.parse(localStorage.getItem('dz_cart') || '[]');
      return new Set(cart.map(it => it?.variant?.supplier_id).filter(Boolean));
    } catch { return new Set(); }
  });
  const { toast } = useToast();

  useEffect(() => {
    const onChange = () => setIds(loadIds());
    window.addEventListener('compare-changed', onChange);
    return () => window.removeEventListener('compare-changed', onChange);
  }, []);

  useEffect(() => {
    const readCart = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('dz_cart') || '[]');
        setCartSupplierIds(new Set(cart.map(it => it?.variant?.supplier_id).filter(Boolean)));
      } catch { setCartSupplierIds(new Set()); }
    };
    window.addEventListener('cart-changed', readCart);
    window.addEventListener('storage', readCart);
    return () => { window.removeEventListener('cart-changed', readCart); window.removeEventListener('storage', readCart); };
  }, []);

  useEffect(() => {
    if (ids.length === 0) { setItems([]); setOffersByProduct({}); return; }
    Promise.all(ids.map((id) => CatalogAPI.product(id).catch(() => null)))
      .then((products) => setItems(products.filter(Boolean)));
    Promise.all(ids.map((id) => CatalogAPI.offers(id).then((d) => [id, d]).catch(() => [id, []])))
      .then((entries) => setOffersByProduct(Object.fromEntries(entries)));
  }, [ids]);

  const remove = (id) => {
    const next = ids.filter((x) => x !== id);
    localStorage.setItem('dz_compare', JSON.stringify(next));
    setIds(next);
    window.dispatchEvent(new Event('compare-changed'));
  };

  const clearAll = () => {
    localStorage.setItem('dz_compare', '[]');
    setIds([]);
    window.dispatchEvent(new Event('compare-changed'));
  };

  const bestOfferOf = (pid) => {
    const offers = offersByProduct[pid] || [];
    const inStock = offers.filter((o) => o.in_stock);
    return inStock[0] || offers[0];
  };

  if (ids.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <GitCompare className="w-12 h-12 text-[#1F4D3F] mx-auto mb-3" />
        <h1 className="font-serif text-2xl font-semibold text-[#1F4D3F]">Aucun produit à comparer</h1>
        <p className="text-[#7A6F5C] mt-2 mb-6">Ajoutez des produits depuis le catalogue (jusqu'à 4) pour les comparer côte à côte.</p>
        <Link to="/categories"><Button className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">Parcourir le catalogue</Button></Link>
      </div>
    );
  }

  const rows = [
    { label: 'Marque', render: (p) => p.brand },
    { label: 'Catégorie', render: (p) => <span className="capitalize">{p.category}</span> },
    { label: 'Note moyenne', render: (p) => (
        <div className="flex items-center gap-1 justify-center">
          <Star className="w-4 h-4 fill-[#E0A52E] text-[#E0A52E]" />
          <span className="font-medium">{p.rating || 0}</span>
          <span className="text-xs text-[#7A6F5C]">({p.reviews || 0})</span>
        </div>
      )},
    { label: 'Nombre d\'offres', render: (p) => <span className="font-medium text-[#1F4D3F]">{(offersByProduct[p.id] || []).length}</span> },
    { label: 'Meilleur prix', render: (p) => {
        const best = bestOfferOf(p.id);
        if (!best) return '—';
        return (
          <div>
            <div className="font-serif text-xl font-semibold text-[#1F4D3F]">{formatPrice(best.price)}</div>
            <div className="text-xs text-[#7A6F5C]">{best.label}</div>
          </div>
        );
      }},
    { label: 'Meilleur fournisseur', render: (p) => {
        const best = bestOfferOf(p.id);
        if (!best?.supplier) return '—';
        const inCart = cartSupplierIds.has(best.supplier_id);
        return (
          <div className="text-xs">
            <div className="font-medium text-[#2D2A26] flex items-center justify-center gap-1 flex-wrap">
              {best.supplier.name}
              {inCart && (
                <span
                  className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider bg-[#1F4D3F] text-white px-1.5 py-0.5 rounded"
                  data-testid={`compare-best-in-cart-${p.id}`}
                  title="Fournisseur déjà dans votre panier"
                >
                  <ShoppingCart className="w-2.5 h-2.5" /> Panier
                </span>
              )}
            </div>
            <div className="text-[#7A6F5C]">{best.supplier.city} · ★ {best.supplier.rating}</div>
          </div>
        );
      }},
    { label: 'Aussi chez votre panier', render: (p) => {
        const offers = offersByProduct[p.id] || [];
        const matching = [];
        const seen = new Set();
        for (const o of offers) {
          if (cartSupplierIds.has(o.supplier_id) && !seen.has(o.supplier_id)) {
            seen.add(o.supplier_id);
            matching.push(o.supplier);
          }
        }
        if (matching.length === 0) {
          return <span className="text-xs text-[#C9BFAE]">—</span>;
        }
        return (
          <div className="flex items-center justify-center gap-1 flex-wrap" data-testid={`compare-cart-suppliers-${p.id}`}>
            {matching.map((s) => (
              <span
                key={s?.id}
                className="inline-flex items-center gap-1 text-[10px] bg-[#1F4D3F]/10 text-[#1F4D3F] border border-[#1F4D3F]/30 px-1.5 py-0.5 rounded"
                title={s?.name}
              >
                <ShoppingCart className="w-2.5 h-2.5" />
                <span className="max-w-[110px] truncate">{s?.name || '—'}</span>
              </span>
            ))}
          </div>
        );
      }},
    { label: 'Fraîcheur', render: (p) => {
        const best = bestOfferOf(p.id);
        if (!best) return '—';
        return <span className={`inline-block text-xs px-2 py-1 rounded border ${freshnessColor(best.freshness)}`}>{formatRelative(best.last_updated)}</span>;
      }},
    { label: 'Stock', render: (p) => {
        const best = bestOfferOf(p.id);
        if (!best) return '—';
        if (!best.in_stock) return <span className="text-xs text-rose-700 font-medium">Rupture</span>;
        return <span className="text-xs text-emerald-700 font-medium">En stock {best.stock ? `(${best.stock})` : ''}</span>;
      }},
    { label: 'Remise', render: (p) => p.discount ? <span className="text-[#C75B27] font-semibold">−{p.discount}%</span> : '—' },
  ];

  const addBest = (p) => {
    const best = bestOfferOf(p.id);
    if (!best || !best.in_stock) { toast({ title: 'Indisponible' }); return; }
    onAddToCart && onAddToCart(p, {
      label: best.label, price: best.price, supplier_id: best.supplier_id, stock: best.stock,
    }, 1);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4">
        <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#2D2A26]">Comparer</span>
      </nav>

      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F]">Comparer les produits</h1>
          <p className="text-[#7A6F5C] mt-1">{items.length} produit{items.length > 1 ? 's' : ''} sélectionné{items.length > 1 ? 's' : ''}</p>
        </div>
        <button onClick={clearAll} className="text-sm text-[#C75B27] hover:underline">Tout effacer</button>
      </div>

      <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr>
              <th className="bg-[#FAF7F2] px-4 py-4 text-left text-xs uppercase tracking-wider text-[#7A6F5C] w-44">Critère</th>
              {items.map((p) => (
                <th key={p.id} className="px-4 py-4 text-center align-top relative min-w-[200px]">
                  <button onClick={() => remove(p.id)} className="absolute top-2 right-2 text-[#7A6F5C] hover:text-[#C75B27]"><X className="w-4 h-4" /></button>
                  <Link to={`/produit/${p.id}`} className="block">
                    <img src={p.image} alt={p.name} className="w-24 h-24 mx-auto rounded-lg object-cover" />
                    <div className="mt-2 text-sm font-medium text-[#2D2A26] hover:text-[#1F4D3F] line-clamp-2">{p.name}</div>
                  </Link>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t border-[#F5F1EA]">
                <td className="bg-[#FAF7F2] px-4 py-3 text-xs uppercase tracking-wider text-[#7A6F5C] font-medium">{r.label}</td>
                {items.map((p) => (
                  <td key={p.id} className="px-4 py-3 text-center text-[#2D2A26]">{r.render(p)}</td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-[#F5F1EA]">
              <td className="bg-[#FAF7F2] px-4 py-3"></td>
              {items.map((p) => (
                <td key={p.id} className="px-4 py-3 text-center">
                  <Button size="sm" onClick={() => addBest(p)} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">
                    <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Meilleur prix
                  </Button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-6 text-xs text-[#7A6F5C] flex items-center gap-2">
        <Award className="w-4 h-4 text-[#C75B27]" />
        Le "meilleur prix" provient automatiquement de l'offre fournisseur la moins chère et disponible.
      </div>
    </div>
  );
};

export default Compare;
